import clsx from "clsx";
import { useId } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Mobile, Warning } from "@/assets/icons/ds";
import {
  DataTable,
  DataTableColumn,
  DataTableRow,
} from "@/components/Interface/DataTable/DataTable.tsx";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip.tsx";
import { Pill } from "@/components/Interface/Pill/Pill.tsx";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer.tsx";
import { TribeMemberInfo } from "@/services/model";

export interface FriendsMembersCardProps {
  dataTestid?: string;
  members: TribeMemberInfo[];
  /** Customer id of the logged-in member, whose row is emphasised and first. */
  currentUserId?: number | string;
  discount: number;
  memberCount: number;
  eligibleSimCount: number;
}

/**
 * The group's key stats as chips plus one row per member
 * with their eligible products, the porting hint and the inactivity warning.
 */
export const FriendsMembersCard = ({
  dataTestid,
  members,
  currentUserId,
  discount,
  memberCount,
  eligibleSimCount,
}: FriendsMembersCardProps) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.groupOverview",
  });
  const titleId = useId();

  const columns: DataTableColumn[] = [
    { key: "name", label: t("name"), className: "w-1/2" },
    { key: "products", label: t("eligibleProducts"), className: "w-1/2" },
  ];

  const isCurrentUser = (member: TribeMemberInfo) =>
    member.userId !== undefined &&
    currentUserId !== undefined &&
    String(member.userId) === String(currentUserId);

  // The logged-in member is shown first, the rest keep the order they come in.
  const sortedMembers = [...members].sort(
    (first, second) =>
      Number(isCurrentUser(second)) - Number(isCurrentUser(first)),
  );

  return (
    <section className="w-full">
      <CardContainer
        dataTestid={dataTestid}
        className="flex w-full flex-col gap-4 rounded-lg p-4"
        variant="ivory"
      >
        <div className="flex w-full flex-wrap items-center gap-3">
          <p
            id={titleId}
            className="w-full min-w-0 p-2 text-body-base font-semibold leading-none tracking-[0.32px] text-foreground-primary tablet:w-auto tablet:flex-1"
          >
            {t("tableTitle")}
          </p>
          <Pill
            className="h-6 bg-surface-info text-foreground-primary-inverse"
            text={`${discount}% ${t("discount")}`}
            size="sm"
          />
          <Pill
            className="h-6 bg-surface-info text-foreground-primary-inverse"
            text={t("members", { count: memberCount })}
            size="sm"
          />
          <Pill
            className="h-6 bg-surface-info text-foreground-primary-inverse"
            text={t("tariffs", { count: eligibleSimCount })}
            size="sm"
          />
        </div>
        <DataTable
          dataTestid="fwb-members-table"
          ariaLabelledBy={titleId}
          className="min-w-[320px]"
          columns={columns}
        >
          {sortedMembers.map((member, index) => {
            const itsMe = isCurrentUser(member);
            const eligibleSims = member.eligibleSims ?? 0;
            const portingSims = member.onpEligibleSims ?? 0;
            const inactiveDays = member.inactiveDays ?? 0;

            return (
              <DataTableRow
                key={member.userId ?? index}
                columns={columns}
                withDivider={index !== sortedMembers.length - 1}
                cells={[
                  <span
                    key="name"
                    className={clsx("truncate", itsMe && "font-semibold")}
                  >
                    {member.firstName || t("newFriend")}
                  </span>,
                  <span key="products" className="flex items-center gap-2">
                    <span className={clsx(itsMe && "font-semibold")}>
                      {eligibleSims}
                    </span>
                    {portingSims > 0 && (
                      <HintTooltip content={t("onpHint")}>
                        <span className="flex items-center gap-2 text-foreground-info">
                          <span>({portingSims})</span>
                          <Mobile className="size-6" />
                        </span>
                      </HintTooltip>
                    )}
                    {inactiveDays > 0 && (
                      <HintTooltip
                        content={
                          <Trans
                            t={t}
                            i18nKey="inactiveWarning"
                            components={[
                              <span key="title" className="font-semibold" />,
                            ]}
                            values={{ days: inactiveDays }}
                          />
                        }
                      >
                        <Warning className="size-6 text-foreground-error" />
                      </HintTooltip>
                    )}
                  </span>,
                ]}
              />
            );
          })}
        </DataTable>
      </CardContainer>
    </section>
  );
};
